import { CheckIcon, Pause as PauseIcon } from 'components/icons';
import { INVOICE_LEARNING_KEYS } from 'pages/invoices/invoiceLearningHints';
import { InvoiceWorkflowBand, InvoiceWorkflowStage } from 'pages/invoices/InvoiceWorkflow';
import { InvoiceStageState } from 'pages/invoices/util/invoiceStages';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { displayName, useLastNameFirst } from 'utils/nameOrder';

const DOT_CLASS: Record<InvoiceStageState, string> = {
    done: 'bg-primary text-primary-foreground',
    current: 'border-primary bg-card border-2',
    paused: 'bg-warning-tint text-warning-ink',
    skipped: 'border-border-control bg-card border-2 border-dashed',
    next: 'border-border-control bg-card border-2',
    ahead: 'border-border-control bg-card border-2',
};

const LABEL_CLASS: Record<InvoiceStageState, string> = {
    done: 'text-foreground font-medium',
    current: 'text-foreground font-semibold',
    paused: 'text-foreground font-semibold',
    skipped: 'text-muted-foreground font-medium',
    next: 'text-muted-foreground font-medium',
    ahead: 'text-muted-foreground font-medium',
};

/** What a screen reader hears after the stage's name; the stages ahead have nothing to add. */
const SPOKEN_STATE_ID: Partial<Record<InvoiceStageState, string>> = {
    done: 'invoices.stepper.done',
    current: 'invoices.stepper.current',
    paused: 'invoices.stepper.current',
    next: 'invoices.stepper.next',
};

const isReachedState = (state: InvoiceStageState) => state === 'done' || state === 'current' || state === 'paused';

const dateAndWho = (stage: InvoiceWorkflowStage) => [stage.date, stage.by].filter(Boolean).join(' · ');

/**
 * The invoice's progress across the band: one equal column per stage, the circles joined by a line that is solid
 * up to the current stage and dashed ahead of it. Each reached stage's caption says when and who, the next one's
 * who it waits for. Below `sm` the words leave the columns for one line under them, which screen readers skip: they
 * read the list.
 */
export function InvoiceStepper({ stages, pending }: Pick<InvoiceWorkflowBand, 'stages' | 'pending'>) {
    const lastNameFirst = useLastNameFirst();
    const pendingText = pending
        ? t('invoices.stepper.pending', {
              role: t(pending.roleId),
              names: pending.employees
                  .map((employee) => displayName({ ...employee, onVacation: false }, lastNameFirst))
                  .join(', '),
          })
        : undefined;
    const currentIndex = stages.findIndex((stage) => stage.state === 'current' || stage.state === 'paused');
    const current = currentIndex >= 0 ? stages[currentIndex] : undefined;
    const next = currentIndex >= 0 ? stages[currentIndex + 1] : undefined;

    return (
        <>
            <ol
                aria-label={t('invoices.stepper.label')}
                data-learning={INVOICE_LEARNING_KEYS.workflow}
                className="border-divider isolate m-0 mt-5 flex w-full list-none border-t p-0 pt-6 pb-3"
            >
                {stages.map((stage, index) => {
                    const spokenStateId = SPOKEN_STATE_ID[stage.state];
                    const caption =
                        stage.state === 'skipped'
                            ? t('invoices.stepper.skipped')
                            : stage.state === 'next'
                              ? pendingText
                              : isReachedState(stage.state)
                                ? dateAndWho(stage)
                                : undefined;
                    return (
                        <li
                            key={stage.status}
                            aria-current={stage.state === 'current' || stage.state === 'paused' ? 'step' : undefined}
                            data-learning={INVOICE_LEARNING_KEYS.step(stage.status)}
                            className="relative flex min-w-0 flex-1 flex-col items-center gap-2 text-center"
                        >
                            {index > 0 ? (
                                <span
                                    aria-hidden
                                    className={cn(
                                        'absolute -start-1/2 end-1/2 top-[11px] border-t-2',
                                        index <= currentIndex ? 'border-primary' : 'border-border-control border-dashed'
                                    )}
                                />
                            ) : null}
                            <span
                                aria-hidden
                                className={cn(
                                    'relative z-10 grid size-6 shrink-0 place-items-center rounded-full',
                                    DOT_CLASS[stage.state]
                                )}
                            >
                                {stage.state === 'done' ? (
                                    <CheckIcon className="size-3.5" />
                                ) : stage.state === 'current' ? (
                                    <span className="bg-primary size-2.5 rounded-full" />
                                ) : stage.state === 'paused' ? (
                                    <PauseIcon className="size-3" />
                                ) : null}
                            </span>
                            <span className="flex min-w-0 flex-col items-center px-1 max-sm:sr-only">
                                <span className={cn('text-meta', LABEL_CLASS[stage.state])}>
                                    {spokenStateId ? (
                                        <>
                                            <span aria-hidden>{stage.label}</span>
                                            <span className="sr-only">{t(spokenStateId, { stage: stage.label })}</span>
                                        </>
                                    ) : (
                                        stage.label
                                    )}
                                </span>
                                {caption ? (
                                    <span
                                        className={cn(
                                            'text-caption tabular-nums',
                                            stage.state === 'next'
                                                ? 'text-foreground-secondary'
                                                : 'text-foreground-tertiary'
                                        )}
                                    >
                                        {caption}
                                    </span>
                                ) : null}
                                {isReachedState(stage.state)
                                    ? stage.details.map((detail, detailIndex) => (
                                          // Two events can share a reason.
                                          <span
                                              key={`${detailIndex}-${detail}`}
                                              className="text-caption text-foreground-tertiary"
                                          >
                                              {detail}
                                          </span>
                                      ))
                                    : null}
                            </span>
                        </li>
                    );
                })}
            </ol>
            <p aria-hidden className="text-meta text-foreground-secondary m-0 flex flex-wrap gap-x-2 pb-3 sm:hidden">
                {current ? (
                    <span className="text-foreground font-semibold">
                        {[current.label, dateAndWho(current)].filter(Boolean).join(' · ')}
                    </span>
                ) : null}
                {next ? <span>{t('invoices.stepper.next-stage', { stage: next.label })}</span> : null}
                {pendingText ? <span>{pendingText}</span> : null}
            </p>
        </>
    );
}
